import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import StepsModal from '@components/StepsModal';
import { Rate, Row, Col, Divider, Input } from 'antd';
import { StarFilled } from '@ant-design/icons';
import { ReactComponent as SatisfactionSurveyIllustration } from '@assets/illustrations/satisfaction-survey.svg';
import { ReactComponent as CompletedResearchIllustration } from '@assets/illustrations/completed-research.svg';
import api from '@services/api';
import SessionContext from '@providers/Session/SessionContext';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import * as S from './styles';
import RateEmoji, { RateEmojiType } from './RateEmoji';

const { TextArea } = Input;

const DEFAULT_ANSWERS = {
  rating: undefined, // q1 = rating 0 to 10
  design: undefined, // q21
  velocity: undefined, // q22
  usability: undefined, // q23
  functionality: undefined, // q24
  suggestions: undefined, // text
};

interface IAnswers {
  rating: number | undefined;
  design: RateEmojiType;
  velocity: RateEmojiType;
  usability: RateEmojiType;
  functionality: RateEmojiType;
  suggestions: string | undefined;
}

const SatisfactionSurvey = () => {
  const { t } = useTranslation('satisfaction-survey');
  const { keycloak } = useContext(KeycloakContext);
  const { userSettings } = useContext(SessionContext);

  const [isOpen, setIsOpen] = useState(false);
  const [answers, setAnswers] = useState<IAnswers>(DEFAULT_ANSWERS);

  const answersToSend = useMemo(() => {
    const { rating, design, velocity, usability, functionality, suggestions } =
      answers;

    return {
      q1: rating,
      q21: design === 'true',
      q22: velocity === 'true',
      q23: usability === 'true',
      q24: functionality === 'true',
      text: suggestions,
    };
  }, [answers]);

  const sessionIdInStorage = useMemo(
    () => localStorage.getItem('sessionId'),
    [],
  );

  useEffect(() => {
    if (
      sessionIdInStorage === null ||
      sessionIdInStorage !== keycloak.sessionId
    ) {
      localStorage.setItem('sessionId', keycloak.sessionId ?? '');
      if (userSettings?.first_login && userSettings?.survey_skip_count >= 0) {
        api
          .get('users/self/survey')
          .then(({ data: showSurvey }) => setIsOpen(showSurvey));
      } else {
        api.put('users/self/settings/', {
          first_login: moment().format('YYYY-MM-DD'),
          survey_skip_count: 0,
        });
      }
    }
  }, [
    keycloak.sessionId,
    sessionIdInStorage,
    userSettings,
    userSettings?.first_login,
    userSettings?.survey_skip_count,
  ]);

  const skipSurvey = useCallback(
    () => api.get('users/self/survey', { params: { skip: true } }),
    [],
  );

  const sendAnswers = useCallback(
    () =>
      api.post('service-proxy/survey/satisfaction', undefined, {
        params: answersToSend,
      }),
    [answersToSend],
  );

  const handleClose = useCallback(() => {
    skipSurvey();
    setIsOpen(false);
    setAnswers(DEFAULT_ANSWERS);
  }, [skipSurvey]);

  const handleSend = useCallback(() => {
    setIsOpen(false);
    sendAnswers();
  }, [sendAnswers]);

  const handleSetAnswers = useCallback(
    (type: string, value: string | number | boolean | undefined) =>
      setAnswers(oldAnswers => ({ ...oldAnswers, [type]: value })),
    [],
  );

  const allQuestionsAnswered = useMemo(() => {
    // suggestion is not mandatory
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { suggestions, ...restAnswers } = answers;
    return Object.values(restAnswers)?.every(val => val);
  }, [answers]);

  const ratingStarCharacter = useCallback(
    (props: any) => (
      <div className="rating-character">
        <span className="rating-character-number">{props.index + 1}</span>
        <StarFilled />
      </div>
    ),
    [],
  );

  const rowsAnswers = useMemo(
    () => [
      {
        text: t('answers.rating'),
        colOne: 24,
        colTwo: 24,
        content: (
          <Rate
            onChange={value => handleSetAnswers('rating', value)}
            character={ratingStarCharacter}
            count={10}
          />
        ),
      },
      {
        text: t('answers.design'),
        content: (
          <RateEmoji
            onChange={value => handleSetAnswers('design', `${value}`)}
          />
        ),
      },
      {
        text: t('answers.velocity'),
        content: (
          <RateEmoji
            onChange={value => handleSetAnswers('velocity', `${value}`)}
          />
        ),
      },
      {
        text: t('answers.usability'),
        content: (
          <RateEmoji
            onChange={value => handleSetAnswers('usability', `${value}`)}
          />
        ),
      },
      {
        text: t('answers.functionality'),
        content: (
          <RateEmoji
            onChange={value => handleSetAnswers('functionality', `${value}`)}
          />
        ),
      },
      {
        text: t('answers.suggestions'),
        colOne: 8,
        colTwo: 16,
        withoutDivider: true,
        content: (
          <TextArea
            rows={4}
            placeholder={t('answers.suggestions-placeholder')}
            onChange={({ target: { value } }) => {
              handleSetAnswers('suggestions', value);
            }}
          />
        ),
      },
    ],
    [t, ratingStarCharacter, handleSetAnswers],
  );

  const modalStep = useMemo(
    () => [
      {
        content: (
          <S.SurveyContainer>
            <S.SurveyHeader>
              <SatisfactionSurveyIllustration />
              <h1>{t('title')}</h1>
              <p>{t('description')}</p>
            </S.SurveyHeader>
            {rowsAnswers?.map(
              ({ text, content, colOne, colTwo, withoutDivider }) => (
                <div key={text}>
                  <Row gutter={[8, 8]} align="middle" justify="space-between">
                    <Col span={colOne ?? 11}>
                      <p>{text}</p>
                    </Col>
                    <Col span={colTwo ?? 11}>{content}</Col>
                  </Row>
                  {!withoutDivider && <Divider />}
                </div>
              ),
            )}
          </S.SurveyContainer>
        ),
      },
    ],
    [t, rowsAnswers],
  );

  const completionStep = useMemo(
    () => (
      <S.CompletedResearchContainer>
        <CompletedResearchIllustration />
        <h1>{t('thanks')}</h1>
        <p>{t('thanks-description')}</p>
      </S.CompletedResearchContainer>
    ),
    [t],
  );

  return (
    <StepsModal
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleSend}
      setIsOpen={setIsOpen}
      alternativeConfirmTextType="to-send"
      steps={modalStep}
      confirmDisabled={!allQuestionsAnswered}
      completionStep={completionStep}
    />
  );
};

export default SatisfactionSurvey;
